/**
 * Projectiles: arrows, bolts, stones, spit and siege shot.
 *
 * Pooled in a structure-of-arrays store and simulated with a light arc so the
 * renderer has something to lean the sprite along. Homing is deliberately weak —
 * volleys should visibly miss a company that sprints out of the beaten zone.
 */

import { MAP_H, MAP_W } from '../constants.ts';
import { alliedWith, areHostile, DamageType, OB_SOLID, Team, UF, WallKind } from '../types.ts';
import { EV, pushEvent } from '../events.ts';
import type { World } from '../world.ts';
import { damageBuilding, damageUnit, damageWall } from './combat.ts';

export const Shot = {
  Arrow: 0,
  Bolt: 1,
  Stone: 2,
  Bullet: 3,
  Spit: 4,
  Siege: 5,
  Magic: 6,
} as const;

/** Flight speed in tiles/sec by shot kind. */
const SHOT_SPEED = [15, 22, 12, 34, 11, 13, 16];
/** How much vertical arc, 0 = flat. */
const SHOT_ARC = [0.55, 0.12, 0.7, 0, 0.6, 0.45, 0.1];

export function spawnShot(
  w: World,
  kind: number,
  fromX: number,
  fromY: number,
  targetH: number,
  tx: number,
  ty: number,
  damage: number,
  dmgType: number,
  team: number,
  splash: number,
  ownerH: number,
): void {
  const i = w.proj.spawn();
  if (i < 0) return;
  const p = w.proj;
  const dx = tx - fromX;
  const dy = ty - fromY;
  const d = Math.hypot(dx, dy) || 1;
  const speed = SHOT_SPEED[kind] ?? 16;

  p.x[i] = fromX;
  p.y[i] = fromY;
  p.z[i] = 0.35;
  p.vx[i] = (dx / d) * speed;
  p.vy[i] = (dy / d) * speed;
  p.vz[i] = (SHOT_ARC[kind] ?? 0.4) * Math.min(2.4, d * 0.34);
  p.ttl[i] = Math.min(4.5, d / speed + 0.9);
  p.damage[i] = damage;
  p.dmgType[i] = dmgType;
  p.team[i] = team;
  p.kind[i] = kind;
  p.splash[i] = splash;
  p.targetH[i] = targetH;
  p.ownerH[i] = ownerH;
  p.tx[i] = tx;
  p.ty[i] = ty;

  pushEvent(w.events, EV.Shoot, fromX, fromY, kind, 0, team, dx / d, dy / d);
}

export function stepProjectiles(w: World, dt: number): void {
  const p = w.proj;
  const u = w.units;
  const high = p.slots.high;

  for (let i = 0; i < high; i++) {
    if (p.slots.alive[i] === 0) continue;

    // Weak homing: re-aim at a live target, but only gently.
    const th = p.targetH[i];
    if (th >= 0) {
      const j = u.resolve(th);
      if (j >= 0) {
        p.tx[i] = u.x[j];
        p.ty[i] = u.y[j];
        const dx = u.x[j] - p.x[i];
        const dy = u.y[j] - p.y[i];
        const d = Math.hypot(dx, dy) || 1;
        const speed = Math.hypot(p.vx[i], p.vy[i]) || 1;
        const turn = Math.min(1, dt * 4.5);
        p.vx[i] += ((dx / d) * speed - p.vx[i]) * turn;
        p.vy[i] += ((dy / d) * speed - p.vy[i]) * turn;
      }
    }

    p.x[i] += p.vx[i] * dt;
    p.y[i] += p.vy[i] * dt;
    p.z[i] += p.vz[i] * dt;
    p.vz[i] -= 5.5 * dt;
    p.ttl[i] -= dt;

    /**
     * Into the rock.
     *
     * The projectile half of the battlefield scatter, and it is deliberately
     * tested on the shot rather than on the shooter: a volley loosed at a man
     * who then runs behind a boulder should hit the boulder, and homing means
     * the shot will happily curve into one. One array read per live shot per
     * tick, and only on a field that has scatter at all — `obstacleGrid` is null
     * everywhere else, so a siege pays a null check.
     *
     * A shot that is *over* the scatter is let through: `z` is height, arcs
     * carry, and a trebuchet stone that stopped against the first tree it
     * passed above would be a siege engine nobody could use in a wood.
     */
    const grid = w.obstacleGrid;
    if (grid !== null && p.z[i] < 1.1) {
      const tx = Math.floor(p.x[i]);
      const ty = Math.floor(p.y[i]);
      if (tx >= 0 && ty >= 0 && tx < MAP_W && ty * MAP_W + tx < grid.length) {
        if ((grid[ty * MAP_W + tx] & OB_SOLID) !== 0) {
          pushEvent(w.events, EV.Impact, p.x[i], p.y[i], 0.3, p.kind[i], p.team[i], p.vx[i], p.vy[i]);
          p.kill(i);
          continue;
        }
      }
    }

    const dxT = p.tx[i] - p.x[i];
    const dyT = p.ty[i] - p.y[i];
    const near = dxT * dxT + dyT * dyT < 0.14;
    const grounded = p.z[i] <= 0;
    const gone = p.x[i] < 0 || p.y[i] < 0 || p.x[i] > MAP_W || p.y[i] > MAP_H;

    if (near || grounded || p.ttl[i] <= 0 || gone) {
      if (!gone) impact(w, i);
      p.kill(i);
    }
  }
}

function impact(w: World, i: number): void {
  const p = w.proj;
  const u = w.units;
  const x = p.x[i];
  const y = p.y[i];
  const team = p.team[i];
  const dmg = p.damage[i];
  const dt = p.dmgType[i];
  const splash = p.splash[i];
  const ownerIdx = u.resolve(p.ownerH[i]);

  pushEvent(w.events, EV.Impact, x, y, splash > 0 ? splash : 0.3, p.kind[i], team, p.vx[i], p.vy[i]);

  if (splash > 0) {
    w.spatial.query(x, y, splash, u, (j, d2) => {
      // Splash spares your own side; it does not spare a bystander, and never
      // did. `alliedWith` rather than `areHostile` on purpose.
      if (alliedWith(u.team[j], team)) return;
      const falloff = 1 - Math.sqrt(d2) / splash;
      damageUnit(w, j, dmg * (0.45 + 0.55 * falloff), dt, x, y, ownerIdx);
    });
    // Splash also chews structures.
    damageStructuresIn(w, x, y, splash, team, dmg * 0.7, dt);
    return;
  }

  const th = p.targetH[i];
  let hit = -1;
  if (th >= 0) {
    const j = u.resolve(th);
    if (j >= 0) {
      const ddx = u.x[j] - x;
      const ddy = u.y[j] - y;
      if (ddx * ddx + ddy * ddy < 0.6) hit = j;
    }
  }
  if (hit < 0) {
    // Nearest enemy at the landing point — a volley that lands still hurts.
    hit = w.spatial.nearest(x, y, 0.55, u, (j) => !alliedWith(u.team[j], team) && u.hp[j] > 0);
  }
  if (hit >= 0) {
    damageUnit(w, hit, dmg, dt, x, y, ownerIdx);
    return;
  }
  damageStructuresIn(w, x, y, 0.35, team, dmg, dt);
}

/**
 * Everything standing that a landing shot can hurt — buildings *and* walls.
 *
 * The wall half was missing entirely, which meant no projectile of any kind
 * could scratch the tile layer. A ballista is flagged `Siege`, so
 * `pickStructureTarget` gives it a wall by preference; it would then hold at
 * bow range, spend its whole quiver on that wall, and do exactly nothing. Only
 * melee could breach, which quietly made every ranged siege engine in the
 * catalogue a decoration.
 *
 * Walls have no owner field: they are the defender's stonework by construction,
 * so only a non-defending team may knock them down. Without that test a
 * player's own tower shot landing short of a breach would help widen it.
 */
function damageStructuresIn(
  w: World,
  x: number,
  y: number,
  radius: number,
  team: number,
  dmg: number,
  dmgType: number,
): void {
  const r = Math.max(0.4, radius);
  const x0 = Math.max(0, Math.floor(x - r));
  const x1 = Math.min(MAP_W - 1, Math.ceil(x + r));
  const y0 = Math.max(0, Math.floor(y - r));
  const y1 = Math.min(MAP_H - 1, Math.ceil(y + r));
  const seen = new Set<number>();
  /**
   * Walls have no owner field: they are the home alliance's stonework by
   * construction, so only a side at war with it may knock them down. Without
   * that test a player's own tower shot landing short of a breach would help
   * widen it — and with `!== Team.Player` it was a test on one banner rather
   * than on a side.
   */
  const canBreach = !alliedWith(team, Team.Player);

  for (let ty = y0; ty <= y1; ty++) {
    for (let tx = x0; tx <= x1; tx++) {
      const k = w.map.idx(tx, ty);
      const b = w.map.building[k];
      if (b >= 0 && !seen.has(b) && !alliedWith(w.buildings.team[b], team)) {
        seen.add(b);
        // Splash hits every structure in the radius for the same damage. There
        // used to be a `def.category === 'defence' ? 1 : 1` here — a ternary
        // whose two branches were the same number, which is a decision somebody
        // meant to make, wrote the shape of, and then never made.
        damageBuilding(w, b, dmg, dmgType);
        continue;
      }
      // A wall tile never also holds a building, so this is not a double hit.
      if (canBreach && w.map.wall[k] !== WallKind.None && w.map.wallHp[k] > 0) {
        damageWall(w, k, dmg, dmgType);
      }
    }
  }
}

/** Fliers pass over walls; used by both the projectile and combat systems. */
export function ignoresWalls(w: World, i: number): boolean {
  return (w.units.flags[i] & UF.Flying) !== 0;
}

/**
 * Kept as the one-line statement of the rule, now delegating.
 *
 * It said what `areHostile` says and was called by nothing, which is how it
 * survived the whole of the two-sided era being subtly wrong about neutrals
 * relative to the targeting sweep beside it. There is one definition now, in
 * `types.ts`, and this is an alias for anybody who imported the old name.
 */
export function isEnemyOf(team: number, other: number): boolean {
  return areHostile(team, other);
}

export const ARROW_TYPES = new Set<number>([DamageType.Arrow, DamageType.Pierce]);
